// components/voice/VoiceMicButton.tsx
import { Mic, VolumeX, Loader2, X, MessageSquare, Radio } from 'lucide-react';
import { ConnectionState } from '../../contexts/voice/types';

interface VoiceMicButtonProps {
  state: ConnectionState;
  muted: boolean;
  onClick?: () => void;
  onMouseDown?: () => void;
  onMouseUp?: () => void;
  onMouseLeave?: () => void;
  onTouchStart?: () => void;
  onTouchEnd?: () => void;
  variant: 'primary' | 'ptt' | 'mute' | 'stop' | 'text';
  size?: 'sm' | 'md' | 'lg';
  title?: string;
  className?: string;
}

export function VoiceMicButton({
  state,
  muted,
  onClick,
  onMouseDown,
  onMouseUp,
  onMouseLeave,
  onTouchStart,
  onTouchEnd,
  variant,
  size = 'md',
  title,
  className = '',
}: VoiceMicButtonProps) {
  const getButtonStyle = () => {
    switch (state) {
      case 'connecting':
        return 'from-rose-400 to-red-500 animate-pulse';
      case 'connected':
        return 'from-green-500 to-emerald-500 animate-pulse';
      case 'speaking':
        return 'from-blue-500 to-cyan-500';
      case 'listening':
        return 'from-rose-500 to-pink-500 animate-pulse';
      default:
        return 'from-primary to-rose-600 hover:from-rose-400 hover:to-rose-700';
    }
  };

  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'p-4',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  // Primary mic button (disconnected state)
  if (variant === 'primary') {
    return (
      <button
        onClick={onClick}
        className={`relative ${sizeClasses[size]} rounded-full bg-gradient-to-br from-primary to-rose-600 shadow-lg transition-all duration-300 transform hover:scale-110 active:scale-95 flex items-center justify-center ${className}`}
        title={title || 'Spraak assistent'}
      >
        <Mic className={`${iconSizes[size]} text-white`} />
      </button>
    );
  }

  // Text chat button
  if (variant === 'text') {
    return (
      <button
        onClick={onClick}
        className={`relative ${sizeClasses[size]} rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg transition-all duration-300 transform hover:scale-110 active:scale-95 flex items-center justify-center ${className}`}
        title={title || 'Text chat'}
      >
        <MessageSquare className={`${iconSizes[size]} text-white`} />
      </button>
    );
  }

  // Push-to-talk button
  if (variant === 'ptt') {
    return (
      <button
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className={`${sizeClasses[size]} rounded-full border flex items-center justify-center transition-all select-none ${
          !muted
            ? 'bg-green-500 border-green-400 text-white shadow-[0_0_15px_rgba(34,197,94,0.5)] scale-105'
            : 'bg-card border-primary/20 text-foreground hover:bg-primary/10'
        } ${className}`}
        title={title || 'Houd ingedrukt om te praten'}
      >
        <Mic className={iconSizes[size]} />
      </button>
    );
  }

  // Mute/unmute button
  if (variant === 'mute') {
    return (
      <button
        onClick={onClick}
        className={`${sizeClasses[size]} rounded-full border flex items-center justify-center transition-all ${
          muted
            ? 'bg-red-500 border-red-400 text-white'
            : 'bg-card border-primary/20 text-foreground hover:bg-primary/10'
        } ${className}`}
        title={title || (muted ? 'Unmute' : 'Mute')}
      >
        {muted ? <VolumeX className={iconSizes[size]} /> : <Mic className={iconSizes[size]} />}
      </button>
    );
  }

  // Stop/disconnect button
  if (variant === 'stop') {
    return (
      <button
        onClick={onClick}
        className={`relative ${sizeClasses[size]} rounded-full bg-gradient-to-br ${getButtonStyle()} shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center justify-center ${
          state === 'listening' ? 'shadow-[0_0_20px_rgba(255,85,85,0.5)]' : ''
        } ${className}`}
        title={title || 'Stop gesprek'}
      >
        {state === 'listening' && (
          <div className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
        )}
        {state === 'connecting' ? (
          <Loader2 className={`${iconSizes[size]} text-white animate-spin`} />
        ) : (
          <X className={`${iconSizes[size]} text-white`} />
        )}
      </button>
    );
  }

  return null;
}

// Push-to-talk mode toggle button
interface PTTToggleButtonProps {
  enabled: boolean;
  onClick: () => void;
  size?: 'sm' | 'md';
  className?: string;
}

export function PTTToggleButton({
  enabled,
  onClick,
  size = 'sm',
  className = '',
}: PTTToggleButtonProps) {
  const sizeClasses = size === 'sm' ? 'w-10 h-10' : 'p-3';

  return (
    <button
      onClick={onClick}
      className={`${sizeClasses} rounded-full border flex items-center justify-center transition-all ${
        enabled
          ? 'bg-blue-500 border-blue-400 text-white'
          : 'bg-card border-primary/20 text-foreground hover:bg-primary/10'
      } ${className}`}
      title={enabled ? 'Push-to-talk AAN' : 'Push-to-talk UIT'}
    >
      <Radio className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} />
    </button>
  );
}
